// src/context/AuthContext.js
import { createContext, useContext, useState } from "react";
import { jwtDecode } from "jwt-decode";
import api from "../api/axios";
import { isGuestUser } from "../utils/guestUser";

const AuthContext = createContext();

export { isGuestUser };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem("user");
    return saved ? JSON.parse(saved) : null;
  });

  const [adminUser, setAdminUser] = useState(() => {
    // Check for both admin and master user data
    const adminData = localStorage.getItem("adminUser");
    return adminData ? JSON.parse(adminData) : null;
  });

  /** ================= User (Frontend) ================= */
  const loginUser = (userData, token) => {
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
    if (token) localStorage.setItem("token", token);
  };

  const logoutUser = async () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        // We're about to clear local state and navigate ourselves
        // regardless of this call's outcome — a failed logout call must
        // never be allowed to trigger axios's global 401/403 hard redirect
        // (window.location.href = "/login" in api/axios.js), which would
        // silently override wherever the caller actually intends to send
        // the user next (e.g. a guest going to /demo-ended, not /login).
        await api.post("/auth/logout", undefined, { skipAuthRedirect: true });
      }
    } catch (e) {
      console.error("User logout error:", e.message || e);
    }
    if (user && user.user_type === "M") {
      // Clear master user data if it’s a master user logging out
      localStorage.removeItem("adminUser");
      localStorage.removeItem("admintoken");
    }
    setUser(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");

    // Clear theme cache
    localStorage.removeItem("theme");
    localStorage.removeItem("theme_cache_time");
    localStorage.removeItem("theme_domain");

    localStorage.removeItem("selectedMainCompany");
    //  navigate("/"); // redirect normal users
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem("user", JSON.stringify(updatedUser));
  };

  /** ================= Admin (Backend Panel) ================= */
  // ---- Admin Login ----
  const loginAdmin = (token) => {
    try {
      const decoded = jwtDecode(token); // decode payload
      //  console.log("AuthContext loginAdmin: Decoded admin user:", decoded); // 🔍 check structure
      setAdminUser(decoded);

      // Use role-based localStorage keys
      if (decoded.role === "M") {
        // Master user
        //   console.log("AuthContext: Setting master user data in localStorage");
        localStorage.setItem('selectedMainCompany', String(decoded.maincompanyid));
        localStorage.setItem("adminUser", JSON.stringify(decoded));
        localStorage.setItem("admintoken", token);
      } else {
        // Admin user (role "A" or others)
        //   console.log("AuthContext: Setting admin user data in localStorage");
        localStorage.setItem("adminUser", JSON.stringify(decoded));
        localStorage.setItem("admintoken", token);
      }
    } catch (error) {
      console.error("Invalid admin token", error);
    }
  };

  const logoutAdmin = async () => {
    try {
      // Check for both admin and master tokens
      const adminToken = localStorage.getItem("admintoken");
      const masterToken = localStorage.getItem("mastertoken");

      if (adminToken) {
        await api.post("/admin/auth/logout");
      } else if (masterToken) {
        await api.post("/admin/auth/logout");
      }
    } catch (e) {
      console.error("Admin logout error:", e.message || e);
    }

    setAdminUser(null);

    // Clear both admin and master localStorage keys
    localStorage.removeItem("selectedMainCompany");
    localStorage.removeItem("adminUser");
    localStorage.removeItem("admintoken");
    //    navigate("/admin"); // redirect admins
  };

  return (
    <AuthContext.Provider
      value={{
        // Normal user
        user,
        loginUser,
        logoutUser,
        updateUser,  // make available globally

        // Admin
        adminUser,
        loginAdmin,
        logoutAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
